import PropTypes from 'prop-types';
import { forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SelectActionButton from 'Components/Atoms/Select/SelectActionButton';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import MultiselectTagsSingleTaxonomy from './MultiselectTagsSingleTaxonomy';
import MultiselectTagsMultipleTaxonomies from './MultiselectTagsMultipleTaxonomies';
import useCategories from 'Hooks/useCategories';
import useTaxonomies from 'Hooks/useTaxonomies';
import Skeleton from 'Components/Atoms/Skeleton/Skeleton';

const MultiselectTags = forwardRef(function MultiselectTags(props, ref) {
    const { onAdd, onChange, ...selectProps } = props;

    const { t } = useTranslation('categories');

    const handleChange = useCallback(
        (tags) => {
            onChange([...new Set(tags)]);
        },
        [onChange],
    );
    const { taxonomies, isLoading: isLoadingTaxonomies } = useTaxonomies();
    const { categories, isLoading: isLoadingCategories } = useCategories();

    const isLoading = isLoadingCategories || isLoadingTaxonomies;
    const hasMultiTaxonomies = taxonomies.length > 1;

    const actions = useMemo(() => {
        if (taxonomies.length === 0) return [];

        if (!onAdd) return [];
        return [
            <SelectActionButton icon={<OutlineAdd size={18} />} key="action-add-tag" onClick={() => onAdd()}>
                {t('add-category')}
            </SelectActionButton>,
        ];
    }, [onAdd, t, taxonomies.length]);

    if (isLoading) return <Skeleton variant="rectangular" />;

    if (hasMultiTaxonomies) {
        return (
            <MultiselectTagsMultipleTaxonomies
                actions={actions}
                categories={categories}
                taxonomies={taxonomies}
                ref={ref}
                onChange={handleChange}
                {...selectProps}
            />
        );
    }

    return (
        <MultiselectTagsSingleTaxonomy
            actions={actions}
            categories={categories}
            ref={ref}
            onChange={handleChange}
            {...selectProps}
        />
    );
});

export default MultiselectTags;

MultiselectTags.propTypes = {
    disabled: PropTypes.bool,
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    className: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    highlight: PropTypes.bool,
    onAdd: PropTypes.func,
};
